import { useCallback } from "react";
import {
  type BrandDisclaimerDefaults,
  resolveBrandDisclaimerDefaults,
} from "@hightouch/lib/ad-studio/disclaimer/brand-defaults";
import { useBrandProfilesListQuery } from "src/graphql/types";

/**
 * Returns a resolver mapping a brand profile id — an ad's project's
 * `brand_profile_id`, or null/unknown — to its AI-disclosure defaults: the named
 * profile if found, else the workspace default, else the shared
 * `DEFAULT_BRAND_DISCLAIMER_DEFAULTS` (handled by `resolveBrandDisclaimerDefaults`).
 *
 * Loads the whole profile list once so a multi-ad modal can resolve each item's
 * defaults without a hook per item. Not suspenseful — until the list loads the
 * resolver returns `DEFAULT_BRAND_DISCLAIMER_DEFAULTS` (via
 * `resolveBrandDisclaimerDefaults`), so callers mounted in always-visible UI
 * (e.g. a toolbar download button) don't suspend.
 */
export function useBrandDisclaimerDefaultsResolver(): (
  brandProfileId: string | null | undefined,
) => BrandDisclaimerDefaults {
  const { data: profiles } = useBrandProfilesListQuery(
    {},
    { select: (d) => d.brand_profiles },
  );

  return useCallback(
    (brandProfileId) => {
      const profile =
        (brandProfileId
          ? profiles?.find((p) => p.id === brandProfileId)
          : undefined) ?? profiles?.find((p) => p.is_default);
      return resolveBrandDisclaimerDefaults(profile?.ai_disclosure_defaults);
    },
    [profiles],
  );
}
